import React, { useState, useEffect } from 'react';
import { 
  DollarSign, TrendingUp, TrendingDown, MapPin, Car, 
  AlertCircle, Info, BarChart, Calendar, Minus, RefreshCw,
  Database, Cloud
} from 'lucide-react';
import api from '../services/api';

export default function MarketValue({ vehicleId, initialMileage = null, initialZipCode = null }) {
  const [mileage, setMileage] = useState(initialMileage || '');
  const [zipCode, setZipCode] = useState(initialZipCode || '');
  const [condition, setCondition] = useState('good');
  const [showHistorical, setShowHistorical] = useState(false);
  const [showForecast, setShowForecast] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);
  
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (vehicleId) {
      fetchMarketValue();
    }
  }, [vehicleId]);

  const fetchMarketValue = async (refresh = false) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await api.get(`/vehicle-profiles/${vehicleId}/market-value`, {
        params: {
          zip_code: zipCode || undefined,
          mileage: mileage || undefined,
          condition,
          include_historical: showHistorical,
          include_forecast: showForecast,
          force_refresh: refresh || forceRefresh
        }
      });
      
      setData(response.data);
      if (forceRefresh) setForceRefresh(false);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchMarketValue();
  };

  const handleForceRefresh = () => {
    setForceRefresh(true);
    fetchMarketValue(true);
  };

  if (error?.response?.data?.error === 'payment_required') {
    return <PaymentRequired data={error.response.data} onPurchase={() => window.location.href = '/billing'} />;
  }

  const valueData = data?.data || data;
  const isFromCache = data?.source === 'cache' || data?.source === 'redis_cache';

  return (
    <div className="space-y-6">
      {/* Input Form */}
      <div className="bg-surface-card rounded-card shadow-elevation-1 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Market Valuation</h2>
          <div className="flex items-center gap-2">
            {data && (
              <CacheIndicator isFromCache={isFromCache} fetchedAt={data.fetched_at} />
            )}
            {!isLoading && (
              <button
                onClick={handleForceRefresh}
                className="p-2 text-ink-muted hover:text-ink-soft hover:bg-surface-muted rounded-lg transition-colors"
                title="Refresh data"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">
                Current Mileage
              </label>
              <input
                type="number"
                value={mileage}
                onChange={(e) => setMileage(e.target.value)}
                placeholder="e.g., 45000"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent border-border-strong"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">
                ZIP Code (optional)
              </label>
              <input
                type="text"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                placeholder="For local pricing"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent border-border-strong"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">
                Condition
              </label>
              <select
                value={condition}
                onChange={(e) => setCondition(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent border-border-strong"
              >
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
              </select>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showHistorical}
                onChange={(e) => setShowHistorical(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">Include 12-month history</span>
            </label>
            
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showForecast}
                onChange={(e) => setShowForecast(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">Include 6-month forecast</span>
            </label>
            
            <button
              type="submit"
              className="ml-auto px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover"
            >
              Update Valuation
            </button>
          </div>
        </form>
      </div>

      {/* Loading State */}
      {isLoading && <ValuationLoadingSkeleton />}

      {/* Error State */}
      {error && !error?.response?.data?.error && (
        <div className="bg-danger-tint rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-danger mx-auto mb-3" />
          <p className="text-danger">{error.message}</p>
        </div>
      )}

      {/* Results */}
      {valueData && (
        <>
          {/* Value Cards */}
          <ValueCards data={valueData} condition={condition} />
          
          {/* Market Analysis */}
          {valueData.market_analysis && <MarketAnalysis analysis={valueData.market_analysis} />}
          
          {/* Local Market (if zip provided) */}
          {valueData.local_market && <LocalMarket market={valueData.local_market} />}
          
          {/* Adjustments */}
          {valueData.adjustments && <ValueAdjustments adjustments={valueData.adjustments} />}
          
          {/* Historical Chart */}
          {valueData.historical_values && <HistoricalChart data={valueData.historical_values} />}
          
          {/* Forecast */}
          {valueData.forecast && <ValueForecast forecast={valueData.forecast} />}
          
          {/* Comparable Vehicles */}
          {valueData.comparables?.length > 0 && <ComparableVehicles vehicles={valueData.comparables} />}
          
          {/* Insights */}
          {valueData.insights?.length > 0 && <ValueInsights insights={valueData.insights} />}
          
          {/* Depreciation Analysis */}
          {valueData.depreciation && <DepreciationAnalysis depreciation={valueData.depreciation} />}
        </>
      )}
    </div>
  );
}

// ========== SUB-COMPONENTS ==========

function CacheIndicator({ isFromCache, fetchedAt }) {
  if (!isFromCache) return null;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Recently';
    const date = new Date(dateStr);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };
  
  return (
    <div className="flex items-center gap-1 text-small text-ink-muted">
      <Database className="w-3 h-3" />
      <span>Cached {formatDate(fetchedAt)}</span>
    </div>
  );
}

function PaymentRequired({ data, onPurchase }) {
  return (
    <div className="bg-warning-tint rounded-lg p-8 text-center">
      <DollarSign className="w-16 h-16 text-warning mx-auto mb-4" />
      <h3 className="text-xl font-semibold mb-2">{data.message}</h3>
      <p className="text-ink-muted mb-6">
        Get comprehensive market valuation with local pricing, trends, and comparables
      </p>
      <div className="flex gap-3 justify-center">
        <button
          onClick={onPurchase}
          className="px-6 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover"
        >
          Pay ${data.price}
        </button>
        <button
          onClick={onPurchase}
          className="px-6 py-2 border border-accent text-accent rounded-lg hover:bg-accent-tint"
        >
          Subscribe for Access
        </button>
      </div>
    </div>
  );
}

function ValueCards({ data, condition }) {
  const values = data.selected_values || data.values;
  
  const cards = [
    {
      title: 'Trade-in Value',
      value: values?.trade_in,
      description: 'What dealers typically offer',
      icon: Car,
      color: 'info'
    },
    {
      title: 'Private Party',
      value: values?.private_party,
      description: 'Selling to individual',
      icon: DollarSign,
      color: 'success',
      highlight: true
    },
    {
      title: 'Dealer Retail',
      value: values?.dealer_retail,
      description: 'Dealer asking price',
      icon: DollarSign,
      color: 'info'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {cards.map((card, idx) => (
        <div
          key={idx}
          className={`bg-surface-card rounded-card shadow-elevation-1 p-6 ${
            card.highlight ? 'ring-2 ring-success' : ''
          }`}
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-sm font-medium text-ink-muted">{card.title}</p>
              <p className="text-small text-ink-muted mt-0.5">{card.description}</p>
            </div>
            <card.icon className={`w-5 h-5 text-${card.color}`} />
          </div>
          
          <p className="text-3xl font-bold text-ink-soft">
            {card.value ? `$${card.value.toLocaleString()}` : 'N/A'}
          </p>
          
          {card.highlight && (
            <p className="text-small text-accent mt-2">Most common transaction type</p>
          )}
        </div>
      ))}
    </div>
  );
}

function MarketAnalysis({ analysis }) {
  const getMarketClasses = (temp) => {
    switch (temp) {
      case 'hot': return 'text-danger';
      case 'warm': return 'text-warning';
      case 'cool': return 'text-info';
      case 'cold': return 'text-info';
      default: return 'text-ink-muted';
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === 'increasing') return <TrendingUp className="w-4 h-4 text-success" />;
    if (trend === 'decreasing') return <TrendingDown className="w-4 h-4 text-danger" />;
    return <Minus className="w-4 h-4 text-ink-muted" />;
  };

  return (
    <div className="bg-surface-card rounded-card shadow-elevation-1 p-6">
      <h3 className="text-lg font-semibold mb-4">Market Analysis</h3>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <p className="text-sm text-ink-muted">Market Temperature</p>
          <p className={`font-semibold ${getMarketClasses(analysis?.market_temperature)} capitalize`}>
            {analysis?.market_temperature || 'Unknown'}
          </p>
        </div>
        
        <div>
          <p className="text-sm text-ink-muted">Price Trend</p>
          <div className="flex items-center gap-1">
            {getTrendIcon(analysis?.price_trend)}
            <span className="font-semibold capitalize">
              {analysis?.price_trend || 'Stable'}
            </span>
          </div>
        </div>
        
        <div>
          <p className="text-sm text-ink-muted">Avg. Days on Market</p>
          <p className="font-semibold">{analysis?.average_days_on_market || 'N/A'}</p>
        </div>
        
        <div>
          <p className="text-sm text-ink-muted">Demand Level</p>
          <p className="font-semibold capitalize">{analysis?.demand_level || 'Unknown'}</p>
        </div>
      </div>
      
      {analysis?.price_confidence_score && (
        <div className="mt-4 pt-4 border-t">
          <div className="flex items-center justify-between">
            <span className="text-sm text-ink-muted">Pricing Confidence</span>
            <div className="flex items-center gap-2">
              <div className="w-32 h-2 bg-surface-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-success"
                  style={{ width: `${analysis.price_confidence_score * 100}%` }}
                />
              </div>
              <span className="text-sm font-medium">
                {(analysis.price_confidence_score * 100).toFixed(0)}%
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LocalMarket({ market }) {
  return (
    <div className="bg-surface-card rounded-card shadow-elevation-1 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Local Market</h3>
        <div className="flex items-center gap-1 text-sm text-ink-muted">
          <MapPin className="w-4 h-4" />
          {market.zip_code} ({market.radius_miles} mi radius)
        </div>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div>
          <p className="text-sm text-ink-muted">Similar Vehicles</p>
          <p className="text-2xl font-bold">{market.similar_vehicles_for_sale || 0}</p>
        </div>
        
        <div>
          <p className="text-sm text-ink-muted">Avg. Asking Price</p>
          <p className="text-2xl font-bold">
            ${market.average_asking_price?.toLocaleString() || 'N/A'}
          </p>
        </div>
        
        <div>
          <p className="text-sm text-ink-muted">Price Range</p>
          <p className="font-semibold">
            ${market.price_range?.min?.toLocaleString() || 0} - 
            ${market.price_range?.max?.toLocaleString() || 0}
          </p>
        </div>
      </div>
      
      {market.market_saturation && (
        <div className="mt-4 pt-4 border-t">
          <p className="text-sm text-ink-muted">
            Market saturation: <span className="font-medium">{market.market_saturation}</span>
          </p>
        </div>
      )}
    </div>
  );
}

function ValueAdjustments({ adjustments }) {
  const adjustmentItems = [
    {
      label: 'Mileage Adjustment',
      value: adjustments?.mileage?.adjustment_amount,
      detail: adjustments?.mileage?.variance_percent 
        ? `${adjustments.mileage.variance_percent > 0 ? '+' : ''}${adjustments.mileage.variance_percent}% vs average`
        : null
    },
    {
      label: 'Location Adjustment',
      value: adjustments?.location?.adjustment_amount,
      detail: adjustments?.location?.market_strength
    },
    {
      label: 'Options/Features',
      value: adjustments?.options
    },
    {
      label: 'Color Adjustment',
      value: adjustments?.color
    }
  ];

  const hasAdjustments = adjustmentItems.some(item => item.value && item.value !== 0);

  if (!hasAdjustments) return null;

  return (
    <div className="bg-surface-card rounded-card shadow-elevation-1 p-6">
      <h3 className="text-lg font-semibold mb-4">Value Adjustments</h3>
      
      <div className="space-y-3">
        {adjustmentItems.map((item, idx) => 
          item.value && item.value !== 0 ? (
            <div key={idx} className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-ink-soft">{item.label}</p>
                {item.detail && (
                  <p className="text-small text-ink-muted">{item.detail}</p>
                )}
              </div>
              <span className={`font-medium ${
                item.value > 0 ? 'text-success' : 'text-danger'
              }`}>
                {item.value > 0 ? '+' : ''}${Math.abs(item.value).toLocaleString()}
              </span>
            </div>
          ) : null
        )}
        
        <div className="pt-3 border-t">
          <div className="flex items-center justify-between">
            <p className="font-medium text-ink-soft">Total Adjustments</p>
            <span className={`text-lg font-bold ${
              adjustments?.total_adjustments > 0 ? 'text-success' : 'text-danger'
            }`}>
              {adjustments?.total_adjustments > 0 ? '+' : ''}
              ${Math.abs(adjustments?.total_adjustments || 0).toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function HistoricalChart({ data }) {
  // In a real app, you'd use a charting library like Recharts
  return (
    <div className="bg-surface-card rounded-card shadow-elevation-1 p-6">
      <h3 className="text-lg font-semibold mb-4">12-Month Price History</h3>
      
      <div className="h-64 flex items-end justify-between gap-2">
        {data.data_points?.map((point, idx) => (
          <div key={idx} className="flex-1 flex flex-col items-center">
            <div
              className="w-full bg-accent rounded-t"
              style={{
                height: `${(point.value / Math.max(...data.data_points.map(p => p.value))) * 100}%`
              }}
            />
            <p className="text-small mt-2">{point.months_ago}mo</p>
          </div>
        ))}
      </div>
      
      {data.trends && (
        <div className="mt-4 pt-4 border-t grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-small text-ink-muted">3-Month</p>
            <p className={`font-semibold ${
              data.trends['3_month_change']?.percent > 0 ? 'text-success' : 'text-danger'
            }`}>
              {data.trends['3_month_change']?.percent > 0 ? '+' : ''}
              {data.trends['3_month_change']?.percent?.toFixed(1)}%
            </p>
          </div>
          <div>
            <p className="text-small text-ink-muted">6-Month</p>
            <p className={`font-semibold ${
              data.trends['6_month_change']?.percent > 0 ? 'text-success' : 'text-danger'
            }`}>
              {data.trends['6_month_change']?.percent > 0 ? '+' : ''}
              {data.trends['6_month_change']?.percent?.toFixed(1)}%
            </p>
          </div>
          <div>
            <p className="text-small text-ink-muted">12-Month</p>
            <p className={`font-semibold ${
              data.trends['12_month_change']?.percent > 0 ? 'text-success' : 'text-danger'
            }`}>
              {data.trends['12_month_change']?.percent > 0 ? '+' : ''}
              {data.trends['12_month_change']?.percent?.toFixed(1)}%
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function ValueForecast({ forecast }) {
  return (
    <div className="bg-surface-card rounded-card shadow-elevation-1 p-6">
      <h3 className="text-lg font-semibold mb-4">6-Month Forecast</h3>
      
      {forecast.best_time_to_sell && (
        <div className="bg-info-tint rounded-lg p-4 mb-4">
          <p className="text-sm font-medium text-info">Best Time to Sell</p>
          <p className="text-info">{forecast.best_time_to_sell}</p>
        </div>
      )}
      
      <div className="grid grid-cols-3 gap-4">
        <div className="text-center">
          <p className="text-sm text-ink-muted">6 Months</p>
          <p className="text-lg font-semibold text-danger">
            -{forecast.depreciation_forecast?.['6_months'] || 0}%
          </p>
        </div>
        <div className="text-center">
          <p className="text-sm text-ink-muted">12 Months</p>
          <p className="text-lg font-semibold text-danger">
            -{forecast.depreciation_forecast?.['12_months'] || 0}%
          </p>
        </div>
        <div className="text-center">
          <p className="text-sm text-ink-muted">24 Months</p>
          <p className="text-lg font-semibold text-danger">
            -{forecast.depreciation_forecast?.['24_months'] || 0}%
          </p>
        </div>
      </div>
      
      {forecast.factors?.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <p className="text-sm font-medium text-ink-soft mb-2">Key Factors:</p>
          <ul className="text-sm text-ink-muted space-y-1">
            {forecast.factors.map((factor, idx) => (
              <li key={idx}>• {factor}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ComparableVehicles({ vehicles }) {
  return (
    <div className="bg-surface-card rounded-card shadow-elevation-1 p-6">
      <h3 className="text-lg font-semibold mb-4">Comparable Listings</h3>
      
      <div className="space-y-3">
        {vehicles.slice(0, 5).map((vehicle, idx) => (
          <div key={idx} className="border rounded-lg p-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium">
                  ${vehicle.price?.toLocaleString()}
                  {vehicle.price_vs_average && (
                    <span className={`ml-2 text-sm ${
                      vehicle.price_vs_average > 0 ? 'text-danger' : 'text-success'
                    }`}>
                      ({vehicle.price_vs_average > 0 ? '+' : ''}{vehicle.price_vs_average}% vs avg)
                    </span>
                  )}
                </p>
                <p className="text-sm text-ink-muted">
                  {vehicle.mileage?.toLocaleString()} mi • {vehicle.condition} • {vehicle.dealer_type}
                </p>
                <p className="text-small text-ink-muted mt-1">
                  {vehicle.distance_miles} mi away • Listed {vehicle.days_on_market} days
                </p>
              </div>
              {vehicle.url && (
                <a
                  href={vehicle.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:text-accent-hover text-sm"
                >
                  View →
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ValueInsights({ insights }) {
  return (
    <div className="bg-info-tint rounded-lg p-6">
      <div className="flex items-start gap-3">
        <Info className="w-5 h-5 text-accent mt-0.5" />
        <div>
          <h3 className="font-semibold text-info mb-2">Market Insights</h3>
          <ul className="space-y-1">
            {insights.map((insight, idx) => (
              <li key={idx} className="text-sm text-info">• {insight}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function DepreciationAnalysis({ depreciation }) {
  if (!depreciation) return null;

  return (
    <div className="bg-surface-card rounded-card shadow-elevation-1 p-6">
      <h3 className="text-lg font-semibold mb-4">Depreciation Analysis</h3>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <p className="text-sm text-ink-muted">Total Depreciation</p>
          <p className="text-xl font-semibold text-danger">
            -${depreciation.total_depreciation?.toLocaleString() || 0}
          </p>
          <p className="text-small text-ink-muted">
            {depreciation.depreciation_percent?.toFixed(1)}% from MSRP
          </p>
        </div>
        
        <div>
          <p className="text-sm text-ink-muted">Annual Rate</p>
          <p className="text-xl font-semibold">
            {depreciation.annual_depreciation_rate?.toFixed(1)}%
          </p>
        </div>
        
        <div>
          <p className="text-sm text-ink-muted">Value in 1 Year</p>
          <p className="text-xl font-semibold">
            ${depreciation.projected_value_1_year?.toLocaleString() || 'N/A'}
          </p>
        </div>
        
        <div>
          <p className="text-sm text-ink-muted">Value in 3 Years</p>
          <p className="text-xl font-semibold">
            ${depreciation.projected_value_3_years?.toLocaleString() || 'N/A'}
          </p>
        </div>
      </div>
    </div>
  );
}

function ValuationLoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-surface-muted h-32 rounded-lg animate-pulse" />
        ))}
      </div>
      <div className="bg-surface-muted h-48 rounded-lg animate-pulse" />
    </div>
  );
}